import { useState, useEffect, useRef } from 'react';
import { useSetAtom, useAtomValue } from 'jotai';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Alert, AlertDescription } from './ui/alert';
import { Skeleton } from './ui/skeleton';
import { Briefcase, AlertCircle, User, Building } from 'lucide-react';
import { toast } from 'sonner';
import { generateEditorialAngleActionAtom } from '../stores/assignmentsStore';
import { sourcesAtom, fetchSourcesActionAtom } from '../stores/sourcesStore';
import { fetchTipsActionAtom } from '../stores/tipsStore';
import { TipResponse } from '@/types/tips';


interface CreateAssignmentDialogProps {
  tip: TipResponse;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CreateAssignmentDialog({
  tip,
  open,
  onOpenChange,
}: CreateAssignmentDialogProps) {
  const [selectedSourceId, setSelectedSourceId] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hasFetchedSourcesRef = useRef(false);

  const sources = useAtomValue(sourcesAtom);
  const generateEditorialAngle = useSetAtom(generateEditorialAngleActionAtom);
  const fetchSources = useSetAtom(fetchSourcesActionAtom);
  const fetchTips = useSetAtom(fetchTipsActionAtom);

  // Fetch sources when dialog opens (once)
  useEffect(() => {
    if (open && !hasFetchedSourcesRef.current) {
      hasFetchedSourcesRef.current = true;
      fetchSources().catch(console.error);
    }
    if (open) {
      setError(null);
      setSelectedSourceId('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleCreateAssignment = async () => {
    if (!selectedSourceId) {
      setError('Please select a source for this assignment');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const result = await generateEditorialAngle(
        tip.id,
        selectedSourceId,
        true
      );

      if (result.assignment) {
        toast.success('Assignment created successfully', {
          description:
            'The tip has been converted to an assignment with AI-generated editorial angle.',
        });

        // Refresh tips list
        fetchTips().catch(console.error);

        // Close dialog
        onOpenChange(false);
      } else {
        throw new Error('Assignment was not created');
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to create assignment';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-[500px]'>
        <DialogHeader>
          <DialogTitle className='flex items-center gap-2'>
            <Briefcase className='h-5 w-5' />
            Create Assignment from Tip
          </DialogTitle>
          <DialogDescription>
            Select a source to associate with this assignment. The AI will
            generate an editorial angle based on the tip and selected source.
          </DialogDescription>
        </DialogHeader>

        <div className='space-y-4 py-4'>
          {/* Tip Information */}
          <div className='p-4 bg-muted rounded-lg space-y-2'>
            <div className='font-medium text-sm text-muted-foreground'>Tip</div>
            <div className='font-semibold'>{tip.title}</div>
            {tip.description && (
              <div className='text-sm text-muted-foreground line-clamp-2'>
                {tip.description}
              </div>
            )}
          </div>

          {/* Source Selection */}
          <div className='space-y-2'>
            <Label htmlFor='source'>Select Source *</Label>
            {sources.length === 0 ? (
              <div className='space-y-2'>
                <Skeleton className='h-10 w-full' />
                <Alert>
                  <AlertCircle className='h-4 w-4' />
                  <AlertDescription>
                    Loading sources... If this persists, you may need to create
                    a source first.
                  </AlertDescription>
                </Alert>
              </div>
            ) : (
              <Select
                value={selectedSourceId}
                onValueChange={setSelectedSourceId}
              >
                <SelectTrigger id='source'>
                  <SelectValue placeholder='Choose a source to interview...' />
                </SelectTrigger>
                <SelectContent>
                  {sources.map(source => (
                    <SelectItem key={source.id} value={source.id}>
                      <div className='flex items-center gap-2'>
                        <User className='h-4 w-4' />
                        <div>
                          <div className='font-medium'>
                            {source.firstName} {source.lastName}
                          </div>
                          {source.company && (
                            <div className='text-xs text-muted-foreground flex items-center gap-1'>
                              <Building className='h-3 w-3' />
                              {source.title
                                ? `${source.title} at ${source.company}`
                                : source.company}
                            </div>
                          )}
                        </div>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <p className='text-sm text-muted-foreground'>
              The source will be the primary contact for this assignment's
              investigation.
            </p>
          </div>

          {/* Error Message */}
          {error && (
            <Alert variant='destructive'>
              <AlertCircle className='h-4 w-4' />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Info about what happens next */}
          <Alert>
            <AlertCircle className='h-4 w-4' />
            <AlertDescription>
              Once created, the AI will:
              <ul className='list-disc list-inside mt-2 space-y-1'>
                <li>Generate a compelling editorial angle</li>
                <li>Create a new assignment with draft status</li>
                <li>Link the tip and source for context</li>
              </ul>
            </AlertDescription>
          </Alert>
        </div>

        <div className='flex justify-end gap-2'>
          <Button
            variant='outline'
            onClick={() => onOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreateAssignment}
            disabled={isSubmitting || !selectedSourceId || sources.length === 0}
            className='gap-2'
          >
            {isSubmitting ? (
              <>
                <div className='h-4 w-4 border-2 border-current border-t-transparent rounded-full animate-spin' />
                Creating...
              </>
            ) : (
              <>
                <Briefcase className='h-4 w-4' />
                Create Assignment
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
